import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { CurrentUser, Language, Market, Vendor } from '@bedouin/shared'
import { resources } from '@/api/resources'
import { ApiRequestError } from '@/api/client'

/**
 * Markets, vendors and languages are read by nearly every view and change
 * rarely, so they are fetched once per session and shared. `ensureLoaded` is
 * safe to call from many components at once -- concurrent callers await the
 * same in-flight promise rather than each firing their own request.
 */
export const useReferenceStore = defineStore('reference', () => {
  const markets = ref<Market[]>([])
  const vendors = ref<Vendor[]>([])
  const languages = ref<Language[]>([])
  const user = ref<CurrentUser | null>(null)

  const loaded = ref(false)
  const error = ref<string | null>(null)
  /**
   * The API's error code alongside the message. The shell branches on
   * `no_access` to explain that the account exists but has no role yet, which
   * is a different situation from the Worker being unreachable.
   */
  const errorCode = ref<string | null>(null)
  let inFlight: Promise<void> | null = null

  async function load(): Promise<void> {
    error.value = null
    errorCode.value = null
    try {
      // Who is asking comes first, because it decides what else may be asked
      // for. Vendors are for the production team (admins and producers) on
      // the API; fetching them in one batch with everything else once failed
      // the whole load for every client account.
      const me = await resources.me()
      user.value = me

      const [marketRows, languageRows, vendorRows] = await Promise.all([
        resources.markets.list(),
        resources.languages.list(),
        me.can_edit ? resources.vendors.list() : Promise.resolve([] as Vendor[]),
      ])
      markets.value = marketRows
      vendors.value = vendorRows
      languages.value = languageRows
      loaded.value = true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Could not load reference data'
      errorCode.value = err instanceof ApiRequestError ? err.code : null
      throw err
    }
  }

  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return
    inFlight ??= load().finally(() => {
      inFlight = null
    })
    // A failed load should not be cached as "attempted"; swallow so a view
    // that only wants dropdown options still renders.
    await inFlight.catch(() => undefined)
  }

  /** Force a refetch after editing a vendor or market in settings. */
  async function reload(): Promise<void> {
    loaded.value = false
    await ensureLoaded()
  }

  /**
   * Two questions the screens ask, answered in one place. `canEdit`: may
   * change project work (admins anywhere, producers on their own clients --
   * the API checks which). `isAdmin`: may reach the areas beyond any one
   * client, such as users, settings and the shared reference data.
   */
  const canEdit = computed(() => user.value?.can_edit === true)
  const isAdmin = computed(() => user.value?.is_admin === true)

  const marketById = computed(() => new Map(markets.value.map((m) => [m.id, m])))
  const vendorById = computed(() => new Map(vendors.value.map((v) => [v.id, v])))
  const languageByCode = computed(() => new Map(languages.value.map((l) => [l.code, l])))

  const marketOptions = computed(() =>
    markets.value.map((market) => ({ value: market.id, label: `${market.code} - ${market.name}` })),
  )
  const vendorOptions = computed(() =>
    vendors.value.map((vendor) => ({ value: vendor.id, label: vendor.name })),
  )
  const languageOptions = computed(() =>
    languages.value.map((language) => ({
      value: language.code,
      label: `${language.name} (${language.code})`,
    })),
  )

  return {
    markets,
    vendors,
    languages,
    user,
    canEdit,
    isAdmin,
    loaded,
    error,
    errorCode,
    ensureLoaded,
    reload,
    marketById,
    vendorById,
    languageByCode,
    marketOptions,
    vendorOptions,
    languageOptions,
  }
})
